<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import DashListHolderNew from 'src/components/Lists/Dashboards/DashListHolderNew.svelte';
	import { dashboards } from 'src/stores/features/dashboards';
	import { edit } from 'src/stores/ui/edit_lists';
	import { createEnergyMonitoringStatsLoader } from '../helpers/energy_monitoring_stats_loader';
	import {
		energyMonitoringControls,
		energyMonitoringDataMode,
		energyMonitoringPeriodId,
		resetEnergyMonitoringStats,
	} from '../helpers/store';
	import { getEnergyDashboardVisualSettings } from '../helpers/energy-dashboard-settings';
	import { energyV2Actions } from './energy-v2-ui';
	import {
		cloneEnergyV2Dashboard,
		ensureEnergyV2Dashboard,
		migrateLegacyCustomEnergyDashboard,
		restoreActiveEnergyV2Purpose,
		switchEnergyV2Purpose,
		updateEnergyV2DashboardVisual,
	} from './energy-v2-dashboard';
	import {
		normalizeEnergyV2Config,
		ENERGY_DASHBOARD_ID,
		PROTECTED_ENERGY_V2_CAPABILITIES,
		type EnergyPurposeId,
	} from './energy-v2-types';
	import { currentUnitId } from 'src/stores/account/home';

	export let dashboardId: string | number = ENERGY_DASHBOARD_ID;

	let ready = false;
	const statsLoader = createEnergyMonitoringStatsLoader();

	$: dashboard = $dashboards[dashboardId];
	$: isProtectedEnergyDashboard = Number(dashboardId) === ENERGY_DASHBOARD_ID;
	$: config = normalizeEnergyV2Config(dashboard?.energyV2, dashboard?.dashWidgets);
	$: renderedDashboard = dashboard
		? {
				...dashboard,
				dashWidgets: config.purposes[config.activePurpose].widgets,
				energyV2: config,
			}
		: undefined;
	$: activePurpose = config.activePurpose;
	$: controls = $energyMonitoringControls;
	$: periodId = $energyMonitoringPeriodId;
	$: (controls,
		periodId,
		$energyMonitoringDataMode,
		$currentUnitId,
		statsLoader.load(controls, periodId));

	const savePurpose = async (purpose: EnergyPurposeId) => {
		await switchEnergyV2Purpose(dashboardId, purpose);
	};

	const cloneCurrent = async () => {
		const visual = getEnergyDashboardVisualSettings(Number(dashboardId));
		const cloneId = await cloneEnergyV2Dashboard(
			dashboardId,
			$format('energy_monitoring_clone_name', { default: 'Energy Monitoring copy' }),
			{
				icon: visual.icon,
				backgroundColor: visual.backgroundColor,
			},
		);
		if (cloneId !== undefined) push(`/dash/${cloneId}`);
	};

	const restoreCurrent = async () => {
		await restoreActiveEnergyV2Purpose(dashboardId);
	};

	const saveVisual = async (visual: { icon: string; backgroundColor: string }) => {
		await updateEnergyV2DashboardVisual(dashboardId, visual);
	};

	const toggleEdit = () => edit.update((value) => !value);

	$: if (ready && isProtectedEnergyDashboard) {
		energyV2Actions.set({
			dashboardId,
			activePurpose,
			protectedDashboard: true,
			editMode: $edit,
			onPurposeSave: savePurpose,
			onToggleEdit: toggleEdit,
			onClone: cloneCurrent,
			onRestore: restoreCurrent,
			onVisualSave: saveVisual,
		});
	}

	onMount(() => {
		void (async () => {
			if (isProtectedEnergyDashboard) {
				await ensureEnergyV2Dashboard();
				ready = true;
				return;
			}
			await migrateLegacyCustomEnergyDashboard(dashboardId);
			// Copies are ordinary dashboards. Completing this migration lets the route hand
			// them back to the standard renderer with normal widget management enabled.
			ready = true;
		})();
	});

	onDestroy(() => {
		energyV2Actions.set(null);
		edit.set(false);
		statsLoader.destroy();
		resetEnergyMonitoringStats();
	});
</script>

{#if ready}
	<DashListHolderNew
		id={dashboardId}
		dashboardOverride={renderedDashboard}
		{...isProtectedEnergyDashboard ? PROTECTED_ENERGY_V2_CAPABILITIES : {}}
		useEnergyPageContext={isProtectedEnergyDashboard}
	/>
{/if}
